import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import DrWeedyLogo from "../assets/DrWeedyLogo";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(90);
  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startResendTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCanResend(false);
    setResendTimer(90);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (step === 2) startResendTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [step]);

  const handleSendEmail = async () => {
    setLoading(true);
    setError("");
    try {
      await apiService.forgotPassword(email);
      setStep(2);
    } catch {
      setError("Não foi possível enviar o código");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    startResendTimer();
    try {
      await apiService.forgotPassword(email);
    } catch {
      setError("Erro ao reenviar código");
    }
  };

  const handleVerifyCode = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiService.verifyResetCode(email, code);
      setResetToken(data.resetToken);
      setStep(3);
    } catch {
      setError("Código inválido ou expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setError("");
    try {
      await apiService.resetPassword(resetToken, newPassword);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch {
      setError("Erro ao redefinir a senha");
    } finally {
      setLoading(false);
    }
  };

  const passwordChecks = {
    minLength: newPassword.length >= 8,
    hasUpper: /[A-Z]/.test(newPassword),
    hasLower: /[a-z]/.test(newPassword),
  };

  const allChecksPassed = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;
  const strengthLabel = ["Fraca", "Média", "Forte"][passwordStrength - 1] || "";
  const strengthColor =
    passwordStrength <= 1 ? "#ef4444" : passwordStrength === 2 ? "#f59e0b" : "#4ade80";

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent" />

      <div className="w-full flex flex-col items-center justify-center px-4 py-16 relative z-10">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-6">
              <DrWeedyLogo width={80} height={80} />
            </div>
            <Typography
              variant="h4"
              sx={{ color: "#ffffff", fontWeight: 700, marginBottom: "8px", fontFamily: "Oxanium, sans-serif" }}
            >
              Recuperar Senha
            </Typography>
          </div>

          <Paper
            elevation={8}
            sx={{
              background: "linear-gradient(135deg, rgba(26, 49, 60, 0.8) 0%, rgba(4, 9, 10, 0.9) 100%)",
              backdropFilter: "blur(20px)",
              border: "2px solid rgba(226, 114, 91, 0.2)",
              borderRadius: "32px",
              padding: "40px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(226, 114, 91, 0.1)",
            }}
          >
            {success ? (
              <Alert
                severity="success"
                sx={{
                  borderRadius: "16px",
                  background: "rgba(74, 222, 128, 0.15)",
                  border: "1px solid rgba(74, 222, 128, 0.3)",
                  color: "#4ade80",
                  "& .MuiAlert-icon": { color: "#4ade80" },
                }}
              >
                Senha redefinida com sucesso! Redirecionando...
              </Alert>
            ) : (
              <>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: "16px",
                      background: "rgba(225, 100, 100, 0.15)",
                      border: "1px solid rgba(225, 100, 100, 0.3)",
                      color: "#e16464",
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {step === 1 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", mb: 1 }}>
                      Digite seu email cadastrado para receber o código de recuperação.
                    </Typography>
                    <TextField
                      fullWidth
                      data-test='email-forgot'
                      type="email"
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      sx={inputSx}
                    />
                    <Button onClick={handleSendEmail} disabled={loading || !email} variant="contained" fullWidth sx={btnSx}>
                      {loading ? <CircularProgress size={28} sx={{ color: "#fff" }} /> : "Enviar Código"}
                    </Button>
                    <Button
                      onClick={() => navigate("/login")}
                      variant="text"
                      fullWidth
                      sx={{ color: "rgba(255,255,255,0.6)", textTransform: "none", fontSize: "15px" }}
                    >
                      Voltar ao login
                    </Button>
                  </Box>
                )}

                {step === 2 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", mb: 1 }}>
                      Enviamos um código de 8 dígitos para <strong style={{ color: "#fff" }}>{email}</strong>.
                    </Typography>
                    <TextField
                      fullWidth
                      data-test='codigo-forgot'
                      label="Código"
                      value={code}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 8);
                        setCode(val);
                      }}
                      inputProps={{ maxLength: 8, inputMode: "numeric" }}
                      disabled={loading}
                      sx={inputSx}
                    />
                    <Button
                      onClick={handleVerifyCode}
                      disabled={loading || code.length !== 8}
                      variant="contained"
                      fullWidth
                      sx={btnSx}
                    >
                      {loading ? <CircularProgress size={28} sx={{ color: "#fff" }} /> : "Validar Código"}
                    </Button>
                    <Button
                      onClick={handleResendCode}
                      disabled={!canResend}
                      variant="text"
                      fullWidth
                      sx={{ color: "rgba(255,255,255,0.6)", textTransform: "none", fontSize: "15px" }}
                    >
                      {canResend ? "Reenviar código" : `Reenviar código em ${resendTimer}s`}
                    </Button>
                  </Box>
                )}

                {step === 3 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <TextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      label="Nova Senha"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: "rgba(255,255,255,0.5)" }}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />

                    {newPassword.length > 0 && (
                      <>
                        <Box>
                          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", mb: 0.5 }}>
                            Força da senha: <strong style={{ color: strengthColor }}>{strengthLabel}</strong>
                          </Typography>
                          <Box sx={{ height: 6, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.1)" }}>
                            <Box
                              sx={{
                                height: "100%",
                                width: `${(passwordStrength / 3) * 100}%`,
                                borderRadius: 3,
                                transition: "width 0.3s",
                                backgroundColor: strengthColor,
                              }}
                            />
                          </Box>
                        </Box>
                        <Box>
                          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "13px", mb: 0.5 }}>
                            A senha deve conter:
                          </Typography>
                          <CheckItem label="Mínimo de 8 caracteres" ok={passwordChecks.minLength} />
                          <CheckItem label="Uma letra maiúscula" ok={passwordChecks.hasUpper} />
                          <CheckItem label="Uma letra minúscula" ok={passwordChecks.hasLower} />
                        </Box>
                      </>
                    )}

                    <TextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      label="Confirmar Nova Senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      error={confirmPassword.length > 0 && !passwordsMatch}
                      helperText={
                        confirmPassword.length > 0 && !passwordsMatch ? "As senhas não coincidem" : ""
                      }
                      sx={inputSx}
                    />

                    <Button
                      onClick={handleResetPassword}
                      disabled={loading || !allChecksPassed || !passwordsMatch}
                      variant="contained"
                      fullWidth
                      sx={btnSx}
                    >
                      {loading ? <CircularProgress size={28} sx={{ color: "#fff" }} /> : "Redefinir Senha"}
                    </Button>
                  </Box>
                )}
              </>
            )}

            {loading && !success && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <CircularProgress size={24} sx={{ color: "#E2725B" }} />
              </Box>
            )}
          </Paper>
        </div>
      </div>
    </div>
  );
}

function CheckItem({ label, ok }: { label: string; ok: boolean }) {
  return (
    <Typography sx={{ fontSize: "13px", color: ok ? "#4ade80" : "rgba(255,255,255,0.5)" }}>
      {ok ? "✔" : "✖"} {label}
    </Typography>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#ffffff",
    fontSize: "17px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)", fontSize: "16px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E2725B" },
  "& .MuiInputLabel-shrink": {
    backgroundColor: "#0f2a35",
    padding: "0 8px",
    borderRadius: "4px",
  },
  "& .MuiFormHelperText-root": { color: "#e16464" },
};

const btnSx = {
  background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
  borderRadius: "24px",
  textTransform: "none" as const,
  fontSize: "18px",
  fontWeight: 700,
  padding: "16px 32px",
  color: "#ffffff",
  border: "2px solid rgba(226, 114, 91, 0.3)",
  boxShadow: "0 8px 24px rgba(226, 114, 91, 0.4)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 32px rgba(226, 114, 91, 0.5)",
    background: "linear-gradient(135deg, #CC3B2A 0%, #E2725B 100%)",
  },
  "&:disabled": {
    background: "rgba(255,255,255,0.1)",
    color: "rgba(255,255,255,0.4)",
  },
};
